import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { FaUndo } from 'react-icons/fa';
import Toggle from 'react-toggle';
import TextSearch from '@/shared/components/input/TextSearch';
import EnumSelectV2 from '@/shared/components/input/EnumSelectV2';
import CompanySelector from '@/shared/components/input/CompanySelector';
import EmployeeTagMultiSelect from '@/modules/hrm/EmployeeTagMultiSelect';
import EmployeeRoleSelector from '@/shared/components/input/EmployeeRoleSelector';
import intlShape from '@/shared/utils/intlShape';
import messages from './employeeMessages';
import api from '@/shared/api';
import config from '@/config';
import enumCache from '@/shared/services/EnumCacheService';

export const defaultFilters = {
  search: null,
  companyId: null,
  includeInvisible: false,
  activeEmployeeFilter: 1,
  employeeTypes: null,
  withLabels: [],
  userRoleId: null,
  customRoleId: null,
};

const EmployeeOverviewFilters = ({ filters, onFilterChange, intl }) => {
  const handleChange = changes => {
    const newFilters = { ...filters, ...changes };
    if (JSON.stringify(newFilters) !== JSON.stringify(filters)) onFilterChange(newFilters);
  };

  const resetFilters = () => {
    onFilterChange({ ...defaultFilters });
  };

  // Check if any filters are active (excluding default values)
  const hasActiveFilters = () => {
    return (
      filters.search !== defaultFilters.search ||
      filters.companyId !== defaultFilters.companyId ||
      filters.includeInvisible !== defaultFilters.includeInvisible ||
      filters.activeEmployeeFilter !== defaultFilters.activeEmployeeFilter ||
      filters.employeeTypes !== defaultFilters.employeeTypes ||
      (filters.withLabels && filters.withLabels.length > 0) ||
      filters.userRoleId !== null ||
      filters.customRoleId !== null
    );
  };

  return (
    <>
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-4">
            <div className="form-group">
              <TextSearch
                value={filters.search || ''}
                className="form-control"
                placeholder={intl.formatMessage(messages.SearchEmployee)}
                onChange={value => handleChange({ search: value })}
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <EnumSelectV2
                enumType="employeeType"
                value={filters.employeeTypes?.[0] ?? ''}
                allowNull
                placeholder={intl.formatMessage(messages.EmployeeType)}
                onChange={value => handleChange({ employeeTypes: [value] })}
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <CompanySelector
                value={filters.companyId || ''}
                onChange={val => handleChange({ companyId: val })}
                placeholder={intl.formatMessage(messages.AllCompanies)}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-4">
            <div className="form-group">
              <EmployeeTagMultiSelect
                value={filters.withLabels || []}
                onChange={val => handleChange({ withLabels: val })}
                placeholder={intl.formatMessage(messages.WithLabels)}
                idProp="name"
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <EnumSelectV2
                enumType="activeEmployeeFilter"
                value={filters.activeEmployeeFilter}
                onChange={val => handleChange({ activeEmployeeFilter: val })}
                allowNull={false}
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <EmployeeRoleSelector
                onChange={selectedRole => {
                  if (selectedRole) {
                    handleChange({
                      userRoleId: selectedRole.role,
                      customRoleId: selectedRole.customRoleId || null,
                    });
                  } else {
                    handleChange({
                      userRoleId: null,
                      customRoleId: null,
                    });
                  }
                }}
                value={{
                  role: filters.userRoleId || null,
                  customRoleId: filters.customRoleId || null,
                }}
                placeholder={intl.formatMessage(messages.AllRoles)}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-4">
            {filters.activeEmployeeFilter !== 1 && (
              <div className="form-group">
                <label className="react-toggle-wrapper margin10T toggle-lg">
                  <Toggle
                    checked={!!filters.includeInvisible}
                    onChange={e => handleChange({ includeInvisible: !!e.target.checked })}
                    icons={false}
                  />
                  <span className="label-text">
                    <FormattedMessage {...messages.ShowNotVisibleInApplication} />
                  </span>
                </label>
              </div>
            )}
          </div>
        </div>

        <div className="flex-row">
          <div className="btn-group save-filters">
            {hasActiveFilters() && (
              <button type="button" className="btn btn-default btn-reset" onClick={resetFilters}>
                <FaUndo className="react-icon" />
                &nbsp;
                <FormattedMessage {...messages.ResetFilters} />
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

EmployeeOverviewFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
};

export default injectIntl(EmployeeOverviewFilters);
